@if (formGroup) {
  <div class="dashboard-presentation-settings">
    <!-- Left Panel - Pages List -->
    <div class="settings-selector-section">
      <app-search-input
        (inputChanged)="onSearchChange($event)"
        [placeholder]="
          'dashboard-presentation-settings.search.type-to-filter-the-list-of-pages'
            | translate
        "
      ></app-search-input>
      <div
        class="general-settings"
        [class.selected]="!selectedPageId"
        [class.has-invalid]="hasGeneralInvalidFields"
        (click)="selectGeneral()"
      >
        <fa-icon [icon]="faGear"></fa-icon>
        <span>{{
          "dashboard-presentation-settings.form.general-settings" | translate
        }}</span>
      </div>
      <div class="pages-list">
        @for (page of filteredPages; track trackByPageId($index, page)) {
          <div
            class="page-item"
            [class.selected]="isPageSelected(page.id)"
            (click)="selectPage(page.id)"
          >
            <div class="page-index">{{ page.page_order }}.</div>
            <div
              class="page-content"
              [class.has-invalid]="hasPageInvalidFields(page.id)"
            >
              <div class="page-header">
                <fa-icon [icon]="faCalendar"></fa-icon>
                <div class="page-time">
                  <div class="time-start">
                    {{
                      $safeNavigationMigration(
                        (getEffectivePeriodForPage(page.id) | async)
                          ?.startTimestamp
                      ) * 1000 | date: "dd.MM.yyyy HH:mm"
                    }}
                  </div>
                  <div class="time-end">
                    {{
                      $safeNavigationMigration(
                        (getEffectivePeriodForPage(page.id) | async)
                          ?.endTimestamp
                      ) * 1000 | date: "dd.MM.yyyy HH:mm"
                    }}
                  </div>
                </div>
                <fa-icon
                  class="play-icon"
                  [icon]="
                    page.is_visible_in_presentation
                      ? faCirclePlay
                      : faCircleStop
                  "
                  [ngClass]="{
                    'in-range': page.is_visible_in_presentation,
                    'out-of-range': !page.is_visible_in_presentation,
                  }"
                  [suiTooltip]="
                    page.is_visible_in_presentation
                      ? ('page-navigator.tooltip.included-in-presentation-range'
                        | translate)
                      : ('page-navigator.tooltip.not-in-presentation-range'
                        | translate)
                  "
                  (click)="onVisibilityIconClick(page, $event)"
                ></fa-icon>
              </div>
              <div class="page-title">{{ page.name }}</div>
              @if (shouldShowPageTimer(page)) {
                <div class="page-duration">
                  <fa-icon [icon]="faClock"></fa-icon>
                  <span>{{ getDisplayDuration(page) }}s</span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
    <!-- Right Panel - Settings Form -->
    <form [formGroup]="formGroup" class="presentation-settings-form">
      <div class="settings-header">
        @if (!selectedPageId) {
          <h5 class="settings-title">
            {{
              "dashboard-presentation-settings.form.presentation-settings"
                | translate
            }}
          </h5>
        }
        @if (selectedPageId) {
          <h5 class="settings-title">
            {{
              "dashboard-presentation-settings.form.dashboard-page-settings"
                | translate
            }}
          </h5>
        }
      </div>
      <!-- Global Presentation Settings -->
      @if (!selectedPageId) {
        <div class="settings-content">
          <!-- Logo Visibility -->
          <div class="form-group">
            <label class="form-label">{{
              "dashboard-presentation-settings.form.logo-visibility" | translate
            }}</label>
            <sui-switch formControlName="show_sensika_logo">
              {{
                "dashboard-presentation-settings.form.show-sensika-logo"
                  | translate
              }}
            </sui-switch>
            <sui-switch formControlName="show_company_logo">
              {{
                "dashboard-presentation-settings.form.show-company-logo"
                  | translate
              }}
            </sui-switch>
          </div>
          <!-- Dashboard Title with Apply to All -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">{{
                "dashboard-presentation-settings.form.dashboard-title"
                  | translate
              }}</label>
              <sui-switch formControlName="dashboard_title_visible">
                {{
                  "dashboard-presentation-settings.form.show-dashboard-title"
                    | translate
                }}
              </sui-switch>
              <div class="control-field">
                <label for="dashboard_title_font_size" sui-label>{{
                  "dashboard-presentation-settings.form.font-size" | translate
                }}</label>
                <sui-input
                  type="number"
                  id="dashboard_title_font_size"
                  formControlName="dashboard_title_font_size"
                  [min]="12"
                  [max]="42"
                ></sui-input>
              </div>
              <div class="control-field">
                <label for="dashboard_title_font_weight" sui-label>{{
                  "dashboard-presentation-settings.form.font-weight" | translate
                }}</label>
                <sui-select
                  id="dashboard_title_font_weight"
                  formControlName="dashboard_title_font_weight"
                >
                  <sui-select-option [value]="400" label="400 (Normal)"
                    >400 (Normal)</sui-select-option
                  >
                  <sui-select-option [value]="500" label="500 (Medium)"
                    >500 (Medium)</sui-select-option
                  >
                  <sui-select-option [value]="600" label="600 (Semi-Bold)"
                    >600 (Semi-Bold)</sui-select-option
                  >
                </sui-select>
              </div>
              <div class="control-field">
                <label for="dashboard_title_color_light" sui-label>{{
                  "dashboard-presentation-settings.form.color-light-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'dashboard_title_color_light'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
              <div class="control-field">
                <label for="dashboard_title_color_dark" sui-label>{{
                  "dashboard-presentation-settings.form.color-dark-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'dashboard_title_color_dark'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
            </div>
          </div>
          <!-- Page Title (Global Defaults) with Apply to All -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">{{
                "dashboard-presentation-settings.form.page-title-global-defaults"
                  | translate
              }}</label>
              <sui-switch formControlName="page_title_visible">
                {{
                  "dashboard-presentation-settings.form.show-page-title"
                    | translate
                }}
              </sui-switch>
              <div class="control-field">
                <label for="page_title_font_size" sui-label>{{
                  "dashboard-presentation-settings.form.font-size" | translate
                }}</label>
                <sui-input
                  type="number"
                  id="page_title_font_size"
                  formControlName="page_title_font_size"
                  [min]="12"
                  [max]="42"
                ></sui-input>
              </div>
              <div class="control-field">
                <label for="page_title_font_weight" sui-label>{{
                  "dashboard-presentation-settings.form.font-weight" | translate
                }}</label>
                <sui-select
                  id="page_title_font_weight"
                  formControlName="page_title_font_weight"
                >
                  <sui-select-option [value]="400" label="400 (Normal)"
                    >400 (Normal)</sui-select-option
                  >
                  <sui-select-option [value]="500" label="500 (Medium)"
                    >500 (Medium)</sui-select-option
                  >
                  <sui-select-option [value]="600" label="600 (Semi-Bold)"
                    >600 (Semi-Bold)</sui-select-option
                  >
                </sui-select>
              </div>
              <div class="control-field">
                <label for="page_title_color_light" sui-label>{{
                  "dashboard-presentation-settings.form.color-light-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'page_title_color_light'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
              <div class="control-field">
                <label for="page_title_color_dark" sui-label>{{
                  "dashboard-presentation-settings.form.color-dark-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'page_title_color_dark'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
            </div>
          </div>
          <!-- Time Period with Apply to All -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">Time Period</label>
              <sui-switch formControlName="time_period_visible">
                {{
                  "dashboard-presentation-settings.form.show-time-period"
                    | translate
                }}
              </sui-switch>
              <div class="control-field">
                <label for="time_period_font_size" sui-label>{{
                  "dashboard-presentation-settings.form.font-size" | translate
                }}</label>
                <sui-input
                  type="number"
                  id="time_period_font_size"
                  formControlName="time_period_font_size"
                  [min]="12"
                  [max]="42"
                ></sui-input>
              </div>
              <div class="control-field">
                <label for="time_period_font_weight" sui-label>{{
                  "dashboard-presentation-settings.form.font-weight" | translate
                }}</label>
                <sui-select
                  id="time_period_font_weight"
                  formControlName="time_period_font_weight"
                >
                  <sui-select-option [value]="400" label="400 (Normal)"
                    >400 (Normal)</sui-select-option
                  >
                  <sui-select-option [value]="500" label="500 (Medium)"
                    >500 (Medium)</sui-select-option
                  >
                  <sui-select-option [value]="600" label="600 (Semi-Bold)"
                    >600 (Semi-Bold)</sui-select-option
                  >
                </sui-select>
              </div>
              <div class="control-field">
                <label for="time_period_color_light" sui-label>{{
                  "dashboard-presentation-settings.form.color-light-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'time_period_color_light'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
              <div class="control-field">
                <label for="time_period_color_dark" sui-label>{{
                  "dashboard-presentation-settings.form.color-dark-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'time_period_color_dark'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
            </div>
          </div>
          <!-- Presentation Controls - show / hide -->
          <div class="form-group">
            <label class="form-label show-controls-label">{{
              "dashboard-presentation-settings.form.presentation-player-controls"
                | translate
            }}</label>
            <sui-switch formControlName="show_controls">
              {{ "dashboard-presentation-settings.form.show" | translate }}
            </sui-switch>
          </div>
          <!-- Pagination with Apply to All -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">{{
                "dashboard-presentation-settings.form.pagination" | translate
              }}</label>
              <sui-switch formControlName="pagination_visible">
                {{
                  "dashboard-presentation-settings.form.show-pagination"
                    | translate
                }}
              </sui-switch>
              <div class="control-field">
                <label for="pagination_font_size" sui-label>{{
                  "dashboard-presentation-settings.form.font-size" | translate
                }}</label>
                <sui-input
                  type="number"
                  id="pagination_font_size"
                  formControlName="pagination_font_size"
                  [min]="12"
                  [max]="42"
                ></sui-input>
              </div>
              <div class="control-field">
                <label for="pagination_font_weight" sui-label>{{
                  "dashboard-presentation-settings.form.font-weight" | translate
                }}</label>
                <sui-select
                  id="pagination_font_weight"
                  formControlName="pagination_font_weight"
                >
                  <sui-select-option [value]="400" label="400 (Normal)"
                    >400 (Normal)</sui-select-option
                  >
                  <sui-select-option [value]="500" label="500 (Medium)"
                    >500 (Medium)</sui-select-option
                  >
                  <sui-select-option [value]="600" label="600 (Semi-Bold)"
                    >600 (Semi-Bold)</sui-select-option
                  >
                </sui-select>
              </div>
              <div class="control-field">
                <label for="pagination_color_light" sui-label>{{
                  "dashboard-presentation-settings.form.color-light-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'pagination_color_light'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
              <div class="control-field">
                <label for="pagination_color_dark" sui-label>{{
                  "dashboard-presentation-settings.form.color-dark-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'pagination_color_dark'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
            </div>
          </div>
          <!-- Timers (Auto-Reload & Next Slide) with Apply to All -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">Auto-Reload & Next Slide Timers</label>
              <div class="control-field">
                <label for="timer_font_size" sui-label>{{
                  "dashboard-presentation-settings.form.font-size-timer"
                    | translate
                }}</label>
                <sui-input
                  type="number"
                  id="timer_font_size"
                  formControlName="timer_font_size"
                  [min]="10"
                  [max]="16"
                ></sui-input>
              </div>
              <div class="control-field">
                <label for="timer_font_weight" sui-label>{{
                  "dashboard-presentation-settings.form.font-weight" | translate
                }}</label>
                <sui-select
                  id="timer_font_weight"
                  formControlName="timer_font_weight"
                >
                  <sui-select-option [value]="400" label="400 (Normal)"
                    >400 (Normal)</sui-select-option
                  >
                  <sui-select-option [value]="500" label="500 (Medium)"
                    >500 (Medium)</sui-select-option
                  >
                  <sui-select-option [value]="600" label="600 (Semi-Bold)"
                    >600 (Semi-Bold)</sui-select-option
                  >
                </sui-select>
              </div>
              <div class="control-field">
                <label for="timer_color_light" sui-label>{{
                  "dashboard-presentation-settings.form.color-light-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'timer_color_light'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
              <div class="control-field">
                <label for="timer_color_dark" sui-label>{{
                  "dashboard-presentation-settings.form.color-dark-theme"
                    | translate
                }}</label>
                <app-color-picker
                  [formControlName]="'timer_color_dark'"
                  [allowNullColor]="false"
                ></app-color-picker>
              </div>
            </div>
          </div>
          <!-- Advance Pages -->
          <div class="form-group">
            <label class="form-label">{{
              "dashboard-presentation-settings.form.general-slide-navigation-mode"
                | translate
            }}</label>
            <div
              class="radio-group"
              sui-radio-group
              formControlName="mode"
              orientation="vertical"
            >
              <sui-radio value="manual">
                {{
                  "dashboard-presentation-settings.form.general-manual"
                    | translate
                }}
              </sui-radio>
              <sui-radio value="auto">
                {{
                  "dashboard-presentation-settings.form.general-autoplay"
                    | translate
                }}
              </sui-radio>
            </div>
          </div>
          <!-- Set Timer For All Pages (only shown in auto mode) -->
          @if (formGroup.get("mode")?.value === "auto") {
            <div class="form-group apply-to-all-section">
              <div class="controls-section-for-synchronization">
                <label for="duration_seconds" class="form-label">
                  {{
                    "dashboard-presentation-settings.form.general-set-timer-duration"
                      | translate
                  }}
                </label>
                <div class="control-field">
                  <sui-input
                    type="number"
                    id="duration_seconds"
                    formControlName="duration_seconds"
                    [min]="minDuration"
                  >
                    <span sui-suffix>s</span>
                  </sui-input>
                </div>
              </div>
              <sui-button
                variant="secondary"
                (click)="applyDurationToAllPages()"
              >
                {{
                  "dashboard-presentation-settings.form.apply-to-all"
                    | translate
                }}
              </sui-button>
            </div>
          }
          <!-- Show Timer -->
          @if (formGroup.get("mode")?.value === "auto") {
            <div class="form-group">
              <label class="form-label">
                {{
                  "dashboard-presentation-settings.form.general-show-timer"
                    | translate
                }}
              </label>
              <sui-switch formControlName="show_timer">
                {{ "dashboard-presentation-settings.form.show" | translate }}
              </sui-switch>
            </div>
          }
        </div>
      }
      <!-- Individual Page Settings -->
      @if (selectedPageId) {
        <div class="settings-content">
          <!-- Set Timer For the Selected Page -->
          <div class="form-group apply-to-all-section">
            <div class="controls-section-for-synchronization">
              <label class="form-label">
                {{
                  "dashboard-presentation-settings.form.page-set-timer"
                    | translate
                }}
              </label>
              <div class="control-field">
                <sui-input
                  type="number"
                  id="page_page_duration_seconds"
                  formControlName="page_page_duration_seconds"
                  [min]="1"
                >
                  <span sui-suffix>s</span>
                </sui-input>
              </div>
            </div>
            <!-- Sync Duration from General Button -->
            <div class="synchronization-section">
              <div class="general-info">
                <strong>{{
                  "dashboard-presentation-settings.form.general-settings"
                    | translate
                }}</strong>
                <div>{{ presentationSettings.duration_seconds }}s</div>
              </div>
              <sui-button
                variant="secondary"
                (click)="resetPageFieldToGlobal('duration_seconds')"
              >
                {{
                  "dashboard-presentation-settings.form.sync-duration-from-general"
                    | translate
                }}
              </sui-button>
            </div>
          </div>
          <!-- Period Picker Section -->
          <div>
            <div class="period-picker-inline">
              @if (companyBounds$ | async; as bounds) {
                <app-date-range-selection
                  [fromDate]="pickerFromDate"
                  [toDate]="pickerToDate"
                  [minDate]="bounds.min"
                  [maxDate]="bounds.max"
                  [presetBar]="true"
                  [timeDetails]="true"
                  (dateRangeSelection)="onPeriodRangeSelection($event)"
                ></app-date-range-selection>
              }
            </div>
            <div class="period-actions">
              <sui-button
                variant="secondary"
                [disabled]="isRevertChangesDisabled"
                (click)="resetPeriodToInitial()"
              >
                {{
                  "dashboard-presentation-settings.form.revert-changes"
                    | translate
                }}
              </sui-button>
              <sui-button
                variant="secondary"
                [disabled]="isUseDashboardDefaultDisabled"
                (click)="useDashboardDefaultPeriod()"
              >
                {{
                  "dashboard-presentation-settings.form.use-dashboard-default"
                    | translate
                }}
              </sui-button>
            </div>
          </div>
        </div>
      }
    </form>
  </div>
}
